import { Buffer } from "buffer";
import fontkit from "fontkit";
import { fetchJSON } from "./Utils";
import {
  ellipsisCharacter,
  nonBreakingWhiteSpaceCharacter,
  spaceCharacter,
} from "./Utils";
import {
  fetchURL as countryFetchURL,
  IMetadatum as ICountryMetadatum,
} from "./workerStore/fetchedData/countryMetadata";
import {
  hsFetchURL,
  IMetadatum as IProductMetadatum,
  sitcFetchURL,
} from "./workerStore/fetchedData/productMetadata";

type APIResponse<T> = { data: T[] };

interface IWithNames {
  name_en: string;
  name_short_en: string;
}
const getAllTextsFromList = (data: IWithNames[]): string[] => {
  const result: string[] = [];
  const dataLength = data.length;
  for (let i = 0; i < dataLength; i += 1) {
    const datum = data[i];
    result.push(datum.name_en, datum.name_short_en);
  }
  return result;
};

const getCharacterSet = async (): Promise<Set<string>> => {
  try {
    const [
      { data: countryMetadata },
      { data: hsMetadata },
      { data: sitcMetadata },
    ] = await Promise.all([
      fetchJSON<APIResponse<ICountryMetadatum>>(countryFetchURL),
      fetchJSON<APIResponse<IProductMetadatum>>(hsFetchURL),
      fetchJSON<APIResponse<IProductMetadatum>>(sitcFetchURL),
    ]);

    const allTexts = [
      ...getAllTextsFromList(countryMetadata),
      ...getAllTextsFromList(hsMetadata),
      ...getAllTextsFromList(sitcMetadata),
    ];

    const characterSet = new Set<string>();
    const allTextsLength = allTexts.length;
    for (let i = 0; i < allTextsLength; i += 1) {
      const text = allTexts[i];
      const textLength = text.length;
      for (let j = 0; j < textLength; j += 1) {
        const character = text[j];
        if (!characterSet.has(character)) {
          characterSet.add(character);
        }
      }
    }
    // Special case: we also need to know the width of the ellipsis character:
    characterSet.add(ellipsisCharacter);
    characterSet.add(spaceCharacter);
    characterSet.add(nonBreakingWhiteSpaceCharacter);
    characterSet.add("h").add("s").add("i").add("t").add("c");
    characterSet
      .add("0")
      .add("1")
      .add("2")
      .add("3")
      .add("4")
      .add("5")
      .add("6")
      .add("7")
      .add("8")
      .add("9");
    characterSet.add("(").add(")");

    return characterSet;
  } catch (e) {
    throw new Error(typeof e === "string" ? e : String(e));
  }
};

// Create a fallback font object with safe methods
const createFallbackFont = () => {
  return {
    unitsPerEm: 1000,
    glyphForCodePoint: (codePoint: number) => ({
      advanceWidth: 500, // Default width for fallback
    }),
    // Add other necessary methods and properties
    getGlyph: () => ({
      advanceWidth: 500,
    }),
    e: () => true, // Add the e method that's causing issues
  };
};

const getFontObject = async (): Promise<any> => {
  try {
    const fontFileResponse = await fetch(
      require("./fonts/source-sans-pro-v13-latin-regular.ttf"),
      { credentials: "include" }
    );
    if (fontFileResponse.ok) {
      const arrayBuffer = await fontFileResponse.arrayBuffer();
      const nodeJSBuffer = new Buffer(arrayBuffer);
      try {
        const font = fontkit.create(nodeJSBuffer);
        return font;
      } catch (error) {
        console.warn("Error creating font:", error);
        return createFallbackFont();
      }
    } else {
      console.warn("Could not fetch font file");
      return createFallbackFont();
    }
  } catch (error) {
    console.warn("Error in getFontObject:", error);
    return createFallbackFont();
  }
};

const fontCache: Map<string, Map<string, number>> = new Map();

const getFontCacheKey = (fontFamily: string, variantName: string) =>
  `${fontFamily}---${variantName}`;

export const getCharacterWidthMap = async (): Promise<Map<string, number>> => {
  try {
    const cacheKey = getFontCacheKey("", "");
    const cachedResult = fontCache.get(cacheKey);
    if (cachedResult === undefined) {
      const result: Map<string, number> = new Map();

      try {
        const [characterSet, fontObject] = await Promise.all([
          getCharacterSet(),
          getFontObject(),
        ]);

        const unitsPerEm = fontObject.unitsPerEm || 1000; // Provide fallback value

        for (const character of characterSet) {
          try {
            const codePoint = character.codePointAt(0);
            if (codePoint === undefined) {
              result.set(character, 0.5); // Default width if codePoint is undefined
              continue;
            }

            let glyph;
            try {
              glyph = fontObject.glyphForCodePoint(codePoint);
            } catch (error) {
              console.warn(
                `Error getting glyph for code point ${codePoint}:`,
                error
              );
              result.set(character, 0.5); // Default width if glyph retrieval fails
              continue;
            }

            if (!glyph || typeof glyph.advanceWidth !== "number") {
              result.set(character, 0.5); // Default width if glyph or advanceWidth is invalid
              continue;
            }

            const width = glyph.advanceWidth / unitsPerEm;
            result.set(character, width);
          } catch (error) {
            console.warn(`Error processing character '${character}':`, error);
            result.set(character, 0.5); // Default width on error
          }
        }
      } catch (error) {
        console.warn("Error in character processing:", error);
        // Provide fallback values for common characters
        const fallbackChars =
          "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 .,;:!?()[]{}+-*/=<>_@#$%^&|~`\"'\\";
        for (let i = 0; i < fallbackChars.length; i++) {
          result.set(fallbackChars[i], 0.5);
        }
      }

      fontCache.set(cacheKey, result);
      return result;
    } else {
      return cachedResult;
    }
  } catch (error) {
    console.warn("Error in getCharacterWidthMap:", error);
    // Return a fallback map with default values
    const fallbackMap = new Map<string, number>();
    const fallbackChars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 .,;:!?()[]{}+-*/=<>_@#$%^&|~`\"'\\";
    for (let i = 0; i < fallbackChars.length; i++) {
      fallbackMap.set(fallbackChars[i], 0.5);
    }
    return fallbackMap;
  }
};
